import React, { useContext, useMemo } from "react";
import { RouteComponentProps } from "react-router";
import { useMediaQuery } from "@material-ui/core";
import {
  GenericDetailsCard,
  GenericDetailsCardComponent,
  GenericDetailsCardComponentEnum,
  GenericDetailsCardTopSectionComponent,
  GenericDetailsCardTopSectionComponentEnum,
  CfarImageDesktop,
  CfarImageMobile,
  IconName,
  useDeviceTypes,
  getTotalPriceText,
  getProductByIndexFiat,
  roundToTwoDecimals,
  getIsExpirationDateEqualToCheckIn,
} from "halifax";
import {
  HotelCfarQuote,
  HotelCfarQuoteAdditionalInfo,
  FREE_CANCELLATION_BY_CAP1_HOTEL_CFAR_POINT_TWO,
  HOTEL_CFAR_POINT_THREE,
  HOTEL_CFAR_TERMS_BODY,
  HOTEL_CFAR_TERMS_TABLE,
  PARTIAL_REFUND_BY_CAP1_HOTEL_CFAR,
  RoomInfoProductsWithTransformedIndexes,
  FiatPrice,
  RewardsPrice,
  RewardsMultipliers,
  RewardsCurrency,
  ValidRewardsCurrency,
} from "redmond";
import dayjs from "dayjs";
import clsx from "clsx";
import { getPortalName } from "@capone/common";
import { ClientContext } from "../../../../../../App";
import { PATH_TERMS_HOTEL_CFAR } from "../../../../../../utils/paths";
import { goToCheckout } from "../../../../../shop/utils/queryStringHelpers";
import type { RefundableRoomsCfarDetailsConnectorProps } from "./container";
import {
  PARTIAL_SCROLL_THRESHOLD,
  LOCATION_TEXT,
  REFUNDABLE_ROOM_TITLE_SEPARATE_LINES,
  REFUNDABLE_ROOM_TITLE,
  SELECT_AND_CONTINUE_BUTTON_COPY,
  STANDARDIZED_REFUNDABLE_ROOM_SUBTITLE,
  REFUNDABLE_ROOM_SUBTITLE_UPDATED_UX,
  REFUNDABLE_ROOM_SUBTITLE,
  COVERAGE_UNDER_100_CFAR_DETAILS,
  CAP1_HOTEL_CFAR_REFUNDABLE_UPON_CANCELLATION,
  CAP1_HOTEL_CFAR_NONREFUNDABLE_UPON_CANCELLATION,
  WARNING_BANNER_GENERIC_COPY,
  READ_TERMS_AND_CONDITIONS_COPY,
  VIEW_TERMS_AND_CONDITIONS_COPY,
  UPGRADE_AND_CONTINUE_BUTTON_COPY,
  CONTINUE_BUTTON_COPY,
  STANDARDIZED_FULLY_REFUNDABLE_CFAR_DETAILS,
  STANDARDIZED_PARTIALLY_REFUNDABLE_CFAR_DETAILS,
  getNthDay,
} from "./constants";

import "./styles.scss";

export interface RefundableRoomsCfarDetailsProps
  extends RefundableRoomsCfarDetailsConnectorProps,
    RouteComponentProps {
  openCfarDetails: boolean;
  onClose?: () => void;
  // Note: Overwrite the default continue action
  onContinue?: () => void;
  contentClassName?: string;
  currentHotelCfarQuote: HotelCfarQuote;
  currentAdditionalInfo: HotelCfarQuoteAdditionalInfo | null;
  roomInfoProduct?: RoomInfoProductsWithTransformedIndexes;
  roomProductIndex?: number;
}

export const RefundableRoomsCfarDetails = (
  props: RefundableRoomsCfarDetailsProps
) => {
  const {
    history,
    openCfarDetails,
    onClose,
    onContinue = () => {
      goToCheckout({ history });
    },
    currentHotelCfarQuote,
    currentAdditionalInfo,
    selectedAccountReferenceId,
    contentClassName,
    hotelShopDateRange,
    roomInfoProduct,
    roomProductIndex,
    selectedLodging,
    isHotelCfarLanguageExperimentEnabled,
  } = props;

  const { isAgentPortal } = useContext(ClientContext);
  const { matchesMobile } = useDeviceTypes();
  const usePartialScroll = useMediaQuery(PARTIAL_SCROLL_THRESHOLD, {
    noSsr: true,
  });

  const includeTaxesAndFees =
    selectedLodging?.price?.displayTaxesAndFeesIncluded;

  const {
    premiumPrices,
    premiumPricesPerNight,
    coveragePercentage,
    coverageAmount: quoteCoverageAmount,
    expirationDate,
  } = currentHotelCfarQuote;

  const isCoverageUnder100Percent = coveragePercentage < 100;
  const isCfarFeeRefundable = !!currentAdditionalInfo?.shouldRefundCfarPremium;
  const isTaxesIncludedInRefundPrice =
    !!currentAdditionalInfo?.totalRefundIncludesTaxes;

  const checkInDate = hotelShopDateRange?.from;
  const checkInTime = selectedLodging?.lodging.checkInInstructions?.checkInTime;

  const isExpirationDateEqualToCheckIn = getIsExpirationDateEqualToCheckIn(
    checkInDate,
    checkInTime,
    expirationDate
  );

  const quoteCoverageAmountFiat = {
    ...premiumPrices.fiat,
    value: quoteCoverageAmount.amount,
  };

  let roomProductDifferenceFiat: FiatPrice;
  let roomProductDifferenceRewards: RewardsPrice;
  const hotelCfarQuoteRewards =
    selectedAccountReferenceId && premiumPricesPerNight
      ? premiumPricesPerNight.rewards[selectedAccountReferenceId]
      : undefined;
  if (roomInfoProduct && roomProductIndex) {
    const currentRoomProductFiat = getProductByIndexFiat(
      roomInfoProduct,
      roomProductIndex,
      includeTaxesAndFees
    );
    const baseRoomProductFiat = getProductByIndexFiat(
      roomInfoProduct,
      0,
      includeTaxesAndFees
    );

    roomProductDifferenceFiat = {
      ...currentRoomProductFiat,
      value: Math.ceil(
        roundToTwoDecimals(
          currentRoomProductFiat.value - baseRoomProductFiat.value
        )
      ),
    };

    // we need to make a synthetic rewards object here. Normally, we get the rewards
    // object from Tysons, but we are actually not displaying the CFAR quote here. We are
    // displaying the difference between this product and the base NR product
    // in an effort to match what gets shown on the hotel shop tile.

    if (hotelCfarQuoteRewards) {
      let multiplier = 100;
      if (
        ValidRewardsCurrency.find(
          (currency) => currency === hotelCfarQuoteRewards.currency
        )
      ) {
        const validCurrency: RewardsCurrency =
          hotelCfarQuoteRewards.currency as RewardsCurrency;
        multiplier = RewardsMultipliers[validCurrency].multiplier;
      }
      roomProductDifferenceRewards = {
        ...hotelCfarQuoteRewards,
        value: roomProductDifferenceFiat.value * multiplier,
      };
    }
  }

  const topContent: GenericDetailsCardTopSectionComponent = {
    className: "hotel-cfar-details-header-with-image",
    image: matchesMobile ? CfarImageMobile : CfarImageDesktop,
    location: LOCATION_TEXT,
    header: matchesMobile
      ? REFUNDABLE_ROOM_TITLE_SEPARATE_LINES
      : REFUNDABLE_ROOM_TITLE,
    component:
      GenericDetailsCardTopSectionComponentEnum.HeaderWithImageBackground,
  };

  const handleViewTerms = () => {
    window.open(PATH_TERMS_HOTEL_CFAR, "_blank")?.focus();
  };

  const mainContent = useMemo(() => {
    const mainContentItems: GenericDetailsCardComponent[] = [];

    // Start continue button
    const continueCta: GenericDetailsCardComponent = {
      message:
        isHotelCfarLanguageExperimentEnabled || isCoverageUnder100Percent
          ? SELECT_AND_CONTINUE_BUTTON_COPY
          : UPGRADE_AND_CONTINUE_BUTTON_COPY,
      onClick: onContinue,
      ariaLabel: UPGRADE_AND_CONTINUE_BUTTON_COPY,
      disabled: false,
      floating: matchesMobile,
      hidden: false,
      fill: "blue",
      component: GenericDetailsCardComponentEnum.GenericCta,
    };
    // End continue button

    // Start subtitle
    if (isHotelCfarLanguageExperimentEnabled || isCoverageUnder100Percent) {
      mainContentItems.push({
        className: clsx(
          "hotel-cfar-details-generic-copy",
          "refundable-room-subtitle",
          "bold"
        ),
        type: "secondary",
        copy: STANDARDIZED_REFUNDABLE_ROOM_SUBTITLE,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    } else {
      const fiatAmount =
        roomProductDifferenceFiat ?? premiumPricesPerNight?.fiat;
      const rewardsAmount =
        roomProductDifferenceRewards ??
        (selectedAccountReferenceId
          ? premiumPricesPerNight?.rewards?.[selectedAccountReferenceId]
          : undefined);

      const fallbackFiat = premiumPrices.fiat;
      const fallbackRewards = selectedAccountReferenceId
        ? premiumPrices.rewards[selectedAccountReferenceId]
        : undefined;

      const copy = premiumPricesPerNight
        ? REFUNDABLE_ROOM_SUBTITLE_UPDATED_UX(
            fiatAmount,
            rewardsAmount,
            true,
            isCfarFeeRefundable
          )
        : REFUNDABLE_ROOM_SUBTITLE(
            fallbackFiat,
            fallbackRewards,
            false,
            isCfarFeeRefundable
          );

      mainContentItems.push({
        className: clsx(
          "hotel-cfar-details-generic-copy",
          "refundable-room-subtitle",
          "bold"
        ),
        type: "secondary",
        copy,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    }
    // End subtitle

    // Start first bullet point
    const shortExpirationDateString = hotelShopDateRange?.from
      ? `${dayjs(hotelShopDateRange.from).format("MMMM D")}${getNthDay(
          dayjs(hotelShopDateRange.from).get("date")
        )}`
      : "";

    const longExpirationDateString = expirationDate
      ? dayjs(expirationDate).format("MMMM D, YYYY [at] h:mm A")
      : "";

    const standardRefundString = currentAdditionalInfo?.standardRefundPrices
      ?.fiat
      ? getTotalPriceText({
          price: currentAdditionalInfo.standardRefundPrices.fiat,
        })
      : "";

    const quoteCoverageAmountString = getTotalPriceText({
      price: quoteCoverageAmountFiat,
    });

    const firstBulletPointIcon = IconName.MoneyCounterClockwiseBlue;

    if (isCoverageUnder100Percent) {
      mainContentItems.push({
        className: "hotel-cfar-details-check-mark-icon",
        type: "secondary",
        copy: COVERAGE_UNDER_100_CFAR_DETAILS({
          expirationDateString: longExpirationDateString,
          expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
          coveragePercentage,
          coverageAmount: quoteCoverageAmountString,
        }),
        icon: firstBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    } else if (isHotelCfarLanguageExperimentEnabled) {
      const firstBulletPointCopy = isCfarFeeRefundable
        ? STANDARDIZED_FULLY_REFUNDABLE_CFAR_DETAILS({
            expirationDateString: longExpirationDateString,
            expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
            coverageAmount: standardRefundString,
            priceIncludesTaxes: isTaxesIncludedInRefundPrice,
          })
        : STANDARDIZED_PARTIALLY_REFUNDABLE_CFAR_DETAILS({
            expirationDateString: longExpirationDateString,
            expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
            coverageAmount: standardRefundString,
            priceIncludesTaxes: isTaxesIncludedInRefundPrice,
          });

      mainContentItems.push({
        className: "hotel-cfar-details-check-mark-icon",
        type: "secondary",
        copy: firstBulletPointCopy,
        icon: firstBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    } else {
      const firstBulletPointCopy = isCfarFeeRefundable
        ? FREE_CANCELLATION_BY_CAP1_HOTEL_CFAR_POINT_TWO(
            shortExpirationDateString,
            standardRefundString,
            isTaxesIncludedInRefundPrice
          )
        : PARTIAL_REFUND_BY_CAP1_HOTEL_CFAR({
            expiryDateString: shortExpirationDateString,
            coverageAmount: standardRefundString,
            excludeTaxesAndFeesCopy: isTaxesIncludedInRefundPrice,
          });

      mainContentItems.push({
        className: "hotel-cfar-details-check-mark-icon",
        type: "secondary",
        copy: firstBulletPointCopy,
        icon: firstBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    }
    // End first bullet point

    // Start second bullet point
    const secondBulletPointCopy = HOTEL_CFAR_POINT_THREE(getPortalName());
    const secondBulletPointIcon = IconName.SimpleLaptopBlue;

    mainContentItems.push({
      className: clsx("hotel-cfar-details-check-mark-icon", "with-money-icon"),
      type: "secondary",
      copy: secondBulletPointCopy,
      icon: secondBulletPointIcon,
      component: GenericDetailsCardComponentEnum.GenericCopy,
    });
    // End second bullet point

    // Start third bullet point
    if (!isCoverageUnder100Percent) {
      const thirdBulletPointCopy = isCfarFeeRefundable
        ? CAP1_HOTEL_CFAR_REFUNDABLE_UPON_CANCELLATION(
            roomProductDifferenceFiat ??
              premiumPricesPerNight?.fiat ??
              premiumPrices.fiat,
            true
          )
        : CAP1_HOTEL_CFAR_NONREFUNDABLE_UPON_CANCELLATION(
            premiumPricesPerNight?.fiat ?? premiumPrices.fiat,
            true
          );
      const thirdBulletPointIcon = isCfarFeeRefundable
        ? IconName.CheckCircleBorder
        : IconName.NotAllowedSignBlue;

      mainContentItems.push({
        className: "hotel-cfar-details-check-mark-icon",
        type: "secondary",
        copy: thirdBulletPointCopy,
        icon: thirdBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    }
    // End third bullet point

    mainContentItems.push({
      copy: WARNING_BANNER_GENERIC_COPY,
      fill: "orange",
      size: "small",
      hideLeftIcon: true,
      hideRightIcon: true,
      useLeftBorder: true,
      component: GenericDetailsCardComponentEnum.NotificationBanner,
    });

    if (!usePartialScroll || matchesMobile) {
      mainContentItems.push(continueCta);
    }

    if (matchesMobile) {
      mainContentItems.push({
        content: [
          {
            title: READ_TERMS_AND_CONDITIONS_COPY,
            body:
              HOTEL_CFAR_TERMS_BODY(getPortalName()) + HOTEL_CFAR_TERMS_TABLE,
          },
        ],
        component: GenericDetailsCardComponentEnum.AccordionGroup,
      });
    } else if (!usePartialScroll) {
      mainContentItems.push({
        message: READ_TERMS_AND_CONDITIONS_COPY,
        onClick: handleViewTerms,
        position: "center",
        disabled: false,
        component: GenericDetailsCardComponentEnum.ClickableLink,
      });
    }

    return mainContentItems;
  }, [
    currentHotelCfarQuote,
    selectedAccountReferenceId,
    isAgentPortal,
    onContinue,
    matchesMobile,
    usePartialScroll,
  ]);

  const bottomContent: GenericDetailsCardComponent[] | undefined =
    useMemo(() => {
      if (!usePartialScroll || matchesMobile) {
        return undefined;
      }

      return [
        {
          message: VIEW_TERMS_AND_CONDITIONS_COPY,
          onClick: handleViewTerms,
          component: GenericDetailsCardComponentEnum.ClickableLink,
        },
        {
          className: clsx(
            "hotel-cfar-details-bottom-banner-continue-button",
            "refundable-room"
          ),
          message: UPGRADE_AND_CONTINUE_BUTTON_COPY,
          onClick: onContinue,
          ariaLabel: CONTINUE_BUTTON_COPY,
          disabled: false,

          floating: false,
          fill: "blue",
          component: GenericDetailsCardComponentEnum.GenericCta,
        },
      ];
    }, [onContinue, matchesMobile, usePartialScroll]);

  return (
    <GenericDetailsCard
      popupClassName="hotel-refundable-room-generic-details-card"
      contentClassName={contentClassName}
      topContent={topContent}
      mainContent={mainContent}
      bottomContent={bottomContent}
      openModal={openCfarDetails}
      onClose={onClose}
      isMobile={matchesMobile}
      contentOnly={false}
      scrollOption={usePartialScroll ? "partial-scroll" : "default-scroll"}
    />
  );
};
